import { gql, type TypedDocumentNode } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import type { GQL } from "@binale-tech/shared";
import { use, useCallback, useMemo } from "react";

import { CompanyContext } from "@domain/context/CompanyContext";

const dmsCreateContacts: TypedDocumentNode<
    Pick<GQL.IMutation, "dmsCreateContacts">,
    GQL.IMutationDmsCreateContactsArgs
> = gql`
    mutation dmsCreateContacts($input: IDsCompanyIDInput!) {
        dmsCreateContacts(input: $input) {
            createdContacts {
                id
                label
                vatNumber
            }
        }
    }
`;

const dmsFillContact: TypedDocumentNode<Pick<GQL.IMutation, "dmsFillContact">, GQL.IMutationDmsFillContactArgs> = gql`
    mutation dmsFillContact($input: IDsCompanyIDInput!) {
        dmsFillContact(input: $input)
    }
`;

type CreateResult = GQL.IMutation["dmsCreateContacts"];
type FillResult = GQL.IMutation["dmsFillContact"];

export const useDmsContactActions = () => {
    const { companyGQL } = use(CompanyContext);
    const [createMutation, { loading: isCreating }] = useMutation(dmsCreateContacts);
    const [fillMutation, { loading: isFilling }] = useMutation(dmsFillContact);
    const companyId = companyGQL?.id;

    const createContacts = useCallback(
        async (ids: string[]): Promise<CreateResult> => {
            if (!companyId) {
                throw new Error("No active company");
            }
            const result = await createMutation({ variables: { input: { companyId, ids } } });
            return result.data?.dmsCreateContacts ?? { createdContacts: [], documents: [] };
        },
        [createMutation, companyId],
    );

    const fillContacts = useCallback(
        async (ids: string[]): Promise<FillResult> => {
            if (!companyId) {
                throw new Error("No active company");
            }
            const result = await fillMutation({ variables: { input: { companyId, ids } } });
            return result.data?.dmsFillContact ?? [];
        },
        [fillMutation, companyId],
    );

    return useMemo(
        () => ({ fillContacts, createContacts, isCreating, isFilling }),
        [fillContacts, createContacts, isCreating, isFilling],
    );
};
